if (!customElements.get("m-scrolling-promotion")) {
  class MScrollingPromotion extends HTMLElement {
    constructor() {
      super();
      this._clones = [];
      this._io = null;
      this._busy = false;
      this._repairCount = 0;

      this._boundSectionLoad = this.onSectionLoad.bind(this);
      this._boundLateLoad = this.debounce(this.verifyAndRepair.bind(this), 300);
      this._boundResize = this.debounce(this.maybeReinit.bind(this), 250);
    }

    connectedCallback() {
      this.promotion = this.querySelector(".m-promotion");
      if (!this.promotion) return;

      document.addEventListener("shopify:section:load", this._boundSectionLoad);
      window.addEventListener("resize", this._boundResize);
      // Images that finish loading late (lazy / CDN) must not leave tracks with different widths
      this.addEventListener("load", this._boundLateLoad, true);

      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener("shopify:section:load", this._boundSectionLoad);
      window.removeEventListener("resize", this._boundResize);
      this.removeEventListener("load", this._boundLateLoad, true);
      if (this._io) this._io.disconnect();
    }

    debounce(fn, wait) {
      let timer = null;
      return function () {
        const args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () {
          fn.apply(null, args);
        }, wait);
      };
    }

    onSectionLoad(event) {
      if (event && event.target && event.target.contains && event.target.contains(this)) {
        this.init();
      }
    }

    async init() {
      if (this._busy) return;
      this._busy = true;
      try {
        this.clearClones();
        this.allTracks().forEach(function (track) {
          track.classList.remove("m-promotion--animated");
        });

        // 1. Wait for the original track's images so width is final before cloning
        await this.ensureTrackImages(this.promotion, false);

        // 2. Create only as many clones as needed to cover the viewport seamlessly
        const singleWidth = this.promotion.getBoundingClientRect().width || 1;
        let needed = Math.max(2, Math.ceil(window.innerWidth / singleWidth) + 1);
        needed = Math.min(needed, 20);

        for (let i = 0; i < needed; i++) {
          const clone = this.promotion.cloneNode(true);
          clone.setAttribute("aria-hidden", "true");
          this.appendChild(clone);
          this._clones.push(clone);
        }

        // 3. Cloned images are lazy by default and never fetch while offscreen inside
        //    the transform-animated track — force them to load eagerly.
        await Promise.all(
          this._clones.map((clone) => this.ensureTrackImages(clone, true))
        );

        // 4. Repair any track whose width differs (cached/small images, late loads)
        await this.alignWidths();

        // 5. Start the animation on every track simultaneously
        this.allTracks().forEach(function (track) {
          track.classList.add("m-promotion--animated");
        });

        this.observeVisibility();
        this._repairCount = 0;
      } finally {
        this._busy = false;
      }
    }

    ensureTrackImages(track, force) {
      const images = Array.from(track.querySelectorAll("img"));
      return Promise.all(
        images.map(
          (img) =>
            new Promise(function (resolve) {
              if (!force && img.complete && img.naturalWidth > 0) return resolve();

              img.setAttribute("loading", "eager");
              img.setAttribute("decoding", "async");

              if (force) {
                const src = img.src;
                img.removeAttribute("src");
                img.src = src;
              }

              let settled = false;
              const done = function () {
                if (!settled) {
                  settled = true;
                  resolve();
                }
              };
              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
              setTimeout(done, 6000);
            })
        )
      );
    }

    allTracks() {
      return Array.from(this.querySelectorAll(".m-promotion"));
    }

    clearClones() {
      this._clones.forEach(function (clone) {
        clone.remove();
      });
      this._clones = [];
    }

    trackWidths() {
      return this.allTracks().map(function (track) {
        return track.getBoundingClientRect().width;
      });
    }

    async alignWidths() {
      for (let attempt = 0; attempt < 2; attempt++) {
        const widths = this.trackWidths();
        const max = Math.max.apply(null, widths);
        const tracks = this.allTracks();
        const bad = tracks.filter(function (_, index) {
          return max - widths[index] > 2;
        });
        if (!bad.length) return true;
        await Promise.all(bad.map((track) => this.ensureTrackImages(track, true)));
        await new Promise(function (r) {
          setTimeout(r, 300);
        });
      }
      return false;
    }

    // Self-heal if images load after start and break width alignment
    async verifyAndRepair() {
      if (this._busy) return;
      const widths = this.trackWidths();
      const max = Math.max.apply(null, widths);
      const misaligned = widths.some(function (w) {
        return Math.abs(w - max) > 2;
      });
      if (misaligned && this._repairCount < 3) {
        this._repairCount++;
        await this.init();
      }
    }

    // Re-build when the viewport grows and existing clones no longer cover it
    maybeReinit() {
      const singleWidth = this.promotion.getBoundingClientRect().width || 1;
      if ((this.allTracks().length - 1) * singleWidth < window.innerWidth) {
        this.init();
      }
    }

    observeVisibility() {
      if (this._io) this._io.disconnect();
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.scrollingPlay();
            } else {
              this.scrollingPause();
            }
          });
        },
        { rootMargin: "0px 0px 50px 0px" }
      );
      this._io.observe(this);
    }

    scrollingPlay() {
      this.classList.remove("m-scrolling-promotion--paused");
    }

    scrollingPause() {
      this.classList.add("m-scrolling-promotion--paused");
    }
  }

  customElements.define("m-scrolling-promotion", MScrollingPromotion);
}
